import React, { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Folder, Check, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import { useCategoriesUnified } from "@/hooks/useCategoriesUnified";

export type HierarchicalCategorySelectorProps = {
  value: string | null;
  onValueChange: (val: string | null) => void;
  categoryType: "inventory" | "store" | "service";
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  showRootOption?: boolean;
};

// Internal type for convenience
type CategoryNode = {
  id: string;
  name: string;
  parentId: string | null;
};

export const HierarchicalCategorySelector: React.FC<HierarchicalCategorySelectorProps> = ({
  value,
  onValueChange,
  categoryType,
  label,
  required = false,
  disabled = false,
  placeholder = "Выберите категорию",
  className,
  showRootOption = true,
}) => {
  const [open, setOpen] = useState(false);
  const { categories, loading } = useCategoriesUnified({ type: categoryType });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Build tree structures
  const { roots, byId, childrenByParent } = useMemo(() => {
    const items: CategoryNode[] = (categories || []).map((c) => ({
      id: c.id!,
      name: c.name,
      parentId: c.parentId ?? null,
    }));

    const byIdMap = new Map<string, CategoryNode>();
    items.forEach((i) => byIdMap.set(i.id, i));

    const rootsList = items.filter((i) => !i.parentId);
    const childrenMap = new Map<string, CategoryNode[]>();
    items.forEach((i) => {
      const pid = i.parentId;
      if (!pid) return;
      const arr = childrenMap.get(pid) ?? [];
      arr.push(i);
      childrenMap.set(pid, arr);
    });

    return { roots: rootsList, byId: byIdMap, childrenByParent: childrenMap };
  }, [categories]);

  // Utility: compute path Parent → Child → ...
  const getPath = (id: string | null): string => {
    if (!id) return "Корневая категория";
    const path: string[] = [];
    let current = id ? byId.get(id) : undefined;
    while (current) {
      path.unshift(current.name);
      current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return path.join(" → ");
  };

  const toggleExpand = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Produce a flat list respecting expanded state
  const renderTree = (nodes: CategoryNode[], level = 0): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    nodes.forEach((node) => {
      const children = childrenByParent.get(node.id) ?? [];
      const hasChildren = children.length > 0;

      result.push(
        <CommandItem
          key={node.id}
          value={`${node.name} ${getPath(node.id)}`}
          onSelect={() => {
            onValueChange(node.id);
            setOpen(false);
          }}
        >
          <Check className={cn("mr-2 h-4 w-4", value === node.id ? "opacity-100" : "opacity-0")} />
          {/* Icons only for parents: chevron indicates expandable */}
          {hasChildren ? (
            <button
              type="button"
              className="mr-2 h-4 w-4 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
              aria-label={expanded[node.id] ? "Collapse" : "Expand"}
            >
              {expanded[node.id] ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="mr-2 h-4 w-4" />
          )}
          <div className="flex flex-col">
            <span className={cn(level > 0 && "pl-" + String(level * 4))}>{node.name}</span>
          </div>
        </CommandItem>
      );

      if (hasChildren && expanded[node.id]) {
        result.push(...renderTree(children, level + 1));
      }
    });
    return result;
  };

  const selectedText = value ? getPath(value) : placeholder;

  if (loading) {
    return (
      <div className={className}>
        {label && (
          <Label className="mb-2 block">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <Label className="mb-2 block">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <div className="flex items-center gap-2 overflow-hidden flex-1">
              <Folder className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{selectedText}</span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder="Поиск категории..." />
            <CommandList>
              <CommandEmpty>Категории не найдены.</CommandEmpty>

              {/* Опция выбора корня (без групповых заголовков) */}
              {showRootOption && (
                <CommandItem
                  value="none"
                  onSelect={() => {
                    onValueChange(null);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                  <span className="mr-2 h-4 w-4" />
                  Корневая категория
                </CommandItem>
              )}

              {/* Плоский список корневых узлов с вложенностями, управляемыми через expanded */}
              {renderTree(roots, 0)}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
